"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import { DrillCatalogCard } from "../drills/catalog-card";
import { request } from "../drills/types";
import { LayoutPreview, LayoutPreviewProvider } from "../layout-preview";
import {
  type CreationAttempt,
  prepareCreationAttempt,
} from "./creation-attempt";
import { SkillError, SkillPicker } from "./picker";
import {
  type SelectionCollection,
  type SelectionCollectionWrite,
  type SkillCategories,
  type SkillDrill,
  type SkillDrills,
  type Weaknesses,
  weaknessLabel,
} from "./types";
import "./skills.css";

export function SkillDrillBrowser({
  weaknessId,
  compact = false,
  newTab = false,
  heading,
  catalog = false,
  onCollectionCreated,
}: {
  weaknessId?: string;
  compact?: boolean;
  newTab?: boolean;
  heading?: string;
  catalog?: boolean;
  onCollectionCreated?: () => void;
}) {
  const [chosenWeakness, setChosenWeakness] = useState("");
  const [initialized, setInitialized] = useState(!!weaknessId);
  const [weaknesses, setWeaknesses] = useState<Weaknesses>([]);
  const [categories, setCategories] = useState<SkillCategories>();
  const [skillIds, setSkillIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<SkillDrills>();
  const [error, setError] = useState("");
  const [filterError, setFilterError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [selected, setSelected] = useState<SkillDrill[]>([]);
  const [title, setTitle] = useState("弱点を練習するドリル集");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState<SelectionCollection>();
  const collectionAttempt =
    useRef<CreationAttempt<SelectionCollectionWrite> | null>(null);
  const pending = useRef(false);
  const activeWeakness = weaknessId ?? chosenWeakness;
  const skillQuery = skillIds.join(",");
  useEffect(() => {
    if (!weaknessId)
      setChosenWeakness(
        new URLSearchParams(window.location.search).get("weakness") ?? "",
      );
    setInitialized(true);
  }, [weaknessId]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt explicitly retries failed filter requests.
  useEffect(() => {
    if (compact) return;
    let active = true;
    Promise.allSettled([
      request<Weaknesses>("/api/weaknesses"),
      request<SkillCategories>("/api/skills"),
    ]).then(([weaknessResult, categoryResult]) => {
      if (!active) return;
      if (weaknessResult.status === "fulfilled")
        setWeaknesses(weaknessResult.value);
      if (categoryResult.status === "fulfilled")
        setCategories(categoryResult.value);
      const failure = [weaknessResult, categoryResult].find(
        (result) => result.status === "rejected",
      );
      if (failure?.status === "rejected")
        setFilterError((failure.reason as Error).message);
      else setFilterError("");
    });
    return () => {
      active = false;
    };
  }, [compact, attempt]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt explicitly retries a failed drill request.
  useEffect(() => {
    if (!initialized) return;
    let active = true;
    setData(undefined);
    setError("");
    const params = new URLSearchParams({ page: String(page) });
    if (activeWeakness) params.set("weaknessId", activeWeakness);
    if (skillQuery) params.set("skillIds", skillQuery);
    request<SkillDrills>(`/api/skill-drills?${params}`)
      .then((result) => {
        if (active) setData(result);
      })
      .catch((cause: Error) => {
        if (active) setError(cause.message);
      });
    return () => {
      active = false;
    };
  }, [activeWeakness, skillQuery, page, initialized, attempt]);
  function toggle(drill: SkillDrill) {
    setSelected((current) =>
      current.some((item) => item.id === drill.id)
        ? current.filter((item) => item.id !== drill.id)
        : [...current, drill],
    );
    setSaved(undefined);
    setSaveError("");
  }
  async function createCollection() {
    if (pending.current || selected.length === 0 || !title.trim()) return;
    pending.current = true;
    setSaving(true);
    setSaveError("");
    const body: SelectionCollectionWrite = {
      title: title.trim(),
      description: "",
      drillIds: selected.map((drill) => drill.id),
    };
    const next = prepareCreationAttempt(body, collectionAttempt.current, () =>
      crypto.randomUUID(),
    );
    collectionAttempt.current = next;
    try {
      setSaved(
        await request<SelectionCollection>(
          `/api/drill-collections/${next.id}/from-drills`,
          next.body,
        ),
      );
      collectionAttempt.current = null;
      setSelected([]);
      onCollectionCreated?.();
    } catch (cause) {
      setSaveError((cause as Error).message);
    } finally {
      pending.current = false;
      setSaving(false);
    }
  }
  const displayed = compact ? data?.drills.slice(0, 3) : data?.drills;
  const PreviewWrapper = compact ? Fragment : LayoutPreviewProvider;
  return (
    <section
      className={`skill-drill-browser${catalog ? " drill-catalog-browser" : ""}`}
      aria-label={activeWeakness ? "弱点に関連するドリル" : "ドリルを探す"}
    >
      <div className="skill-drill-heading">
        <div>
          <h2>
            {heading ??
              (activeWeakness ? "あなたにおすすめのドリル" : "ドリルを探す")}
          </h2>
          <p>
            {activeWeakness
              ? "弱点と共通のスキルがあるドリルです。配置と成功条件を確認して選びましょう。"
              : catalog
                ? "自分で作ったドリルも、購入・公開されたドリルも、ここから選べます。"
                : "練習したいスキルから、取り組むドリルを選びましょう。"}
          </p>
        </div>
        {compact && (
          <a
            className="text-link"
            href={`/drills?weakness=${encodeURIComponent(activeWeakness)}`}
            target={newTab ? "_blank" : undefined}
            rel={newTab ? "noreferrer" : undefined}
          >
            {newTab ? "ドリル一覧を別のタブで開く ↗" : "ドリル一覧で見る →"}
          </a>
        )}
      </div>
      {!compact && (
        <>
          {!weaknessId && (
            <div className="skill-drill-toolbar">
              <label>
                弱点で絞り込み
                <select
                  value={chosenWeakness}
                  onChange={(event) => {
                    setChosenWeakness(event.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">すべてのドリル</option>
                  <option value="all">登録したすべての弱点</option>
                  {weaknesses.map((weakness) => (
                    <option key={weakness.id} value={weakness.id}>
                      {weaknessLabel(weakness)}
                    </option>
                  ))}
                </select>
              </label>
              <a className="text-link" href="/weaknesses">
                弱点を管理
              </a>
            </div>
          )}
          <details className="skill-drill-filter">
            <summary>
              スキルを指定して絞り込む
              {skillIds.length > 0 && `（${skillIds.length}件）`}
            </summary>
            <p>選んだスキルをすべて含むドリルを表示します。</p>
            {categories ? (
              <SkillPicker
                categories={categories}
                selectedIds={skillIds}
                onChange={(ids) => {
                  setSkillIds(ids);
                  setPage(1);
                }}
              />
            ) : (
              <p>スキルを読み込み中…</p>
            )}
          </details>
          {filterError && <SkillError message={filterError} />}
        </>
      )}
      {error && (
        <>
          <SkillError message={error} />
          <button
            type="button"
            className="secondary-button"
            onClick={() => setAttempt((value) => value + 1)}
          >
            再読み込み
          </button>
        </>
      )}
      {!data && !error && <p role="status">ドリルを読み込み中…</p>}
      {data && (
        <>
          <p className="skill-picker-count" role="status">
            {data.total}件のドリル
            {!compact && selected.length > 0 && ` · ${selected.length}件選択中`}
          </p>
          {data.drills.length === 0 && (
            <div className="skill-drill-empty">
              <p>
                {activeWeakness === "all"
                  ? "登録した弱点に関連するドリルはまだありません。"
                  : activeWeakness
                    ? "この弱点に関連するドリルはまだありません。"
                    : "条件に合うドリルはありません。"}
              </p>
              <p>
                {activeWeakness || skillIds.length > 0
                  ? "弱点やスキルの絞り込みを変えると、ほかのドリルも探せます。"
                  : "ドリルを作成するか、ストアで練習教材を探しましょう。"}
              </p>
            </div>
          )}
          <PreviewWrapper>
            <div
              className={
                catalog ? "library-design-drill-list" : "skill-drill-results"
              }
            >
              {displayed?.map((drill) =>
                catalog ? (
                  <DrillCatalogCard
                    key={drill.id}
                    drill={drill}
                    skills={activeWeakness ? drill.matchedSkills : drill.skills}
                  >
                    <label className="skill-drill-check">
                      <input
                        type="checkbox"
                        checked={selected.some((item) => item.id === drill.id)}
                        disabled={
                          saving ||
                          (selected.length >= 100 &&
                            !selected.some((item) => item.id === drill.id))
                        }
                        onChange={() => toggle(drill)}
                      />
                      <span>
                        ドリル集にまとめる
                        <span className="drill-sr-only">：{drill.title}</span>
                      </span>
                    </label>
                  </DrillCatalogCard>
                ) : (
                  <article className="skill-drill-result" key={drill.id}>
                    {!compact && (
                      <label className="skill-drill-check">
                        <input
                          type="checkbox"
                          checked={selected.some(
                            (item) => item.id === drill.id,
                          )}
                          disabled={
                            saving ||
                            (selected.length >= 100 &&
                              !selected.some((item) => item.id === drill.id))
                          }
                          onChange={() => toggle(drill)}
                        />
                        <span>ドリル集にまとめる</span>
                      </label>
                    )}
                    {!compact && (
                      <LayoutPreview
                        id={drill.layout.id}
                        revision={drill.layout.revision}
                        title={drill.title}
                        href={null}
                      />
                    )}
                    <h3>
                      <a
                        href={`/drills/${drill.id}`}
                        target={newTab ? "_blank" : undefined}
                        rel={newTab ? "noreferrer" : undefined}
                      >
                        {drill.title}
                        {newTab && " ↗"}
                      </a>
                    </h3>
                    <p>
                      {drill.goal || "配置と成功条件を確認して練習しましょう。"}
                    </p>
                    {(activeWeakness ? drill.matchedSkills : drill.skills)
                      .length > 0 && (
                      <div>
                        <p>
                          {activeWeakness
                            ? "弱点と共通のスキル"
                            : "鍛えられるスキル"}
                        </p>
                        <div className="weakness-skill-labels">
                          {(activeWeakness
                            ? drill.matchedSkills
                            : drill.skills
                          ).map((skill) => (
                            <span
                              className="weakness-skill-label"
                              key={skill.id}
                              title={skill.categoryLabel}
                            >
                              {skill.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </article>
                ),
              )}
            </div>
          </PreviewWrapper>
          {!compact && data.total > data.pageSize && (
            <div className="skill-drill-pagination">
              <button
                type="button"
                className="secondary-button"
                disabled={page <= 1}
                onClick={() => setPage((value) => value - 1)}
              >
                前へ
              </button>
              <span>
                {data.page} /{" "}
                {Math.max(1, Math.ceil(data.total / data.pageSize))}
              </span>
              <button
                type="button"
                className="secondary-button"
                disabled={data.page * data.pageSize >= data.total}
                onClick={() => setPage((value) => value + 1)}
              >
                次へ
              </button>
            </div>
          )}
        </>
      )}
      {!compact && selected.length > 0 && (
        <div className="skill-drill-selection">
          <h3>選択した{selected.length}件をドリル集にまとめる</h3>
          <p className="skill-picker-count">
            元のドリルと練習設定を使って練習できます。
          </p>
          <div className="weakness-skill-labels">
            {selected.map((drill) => (
              <button
                type="button"
                className="skill-chip"
                key={drill.id}
                disabled={saving}
                onClick={() => toggle(drill)}
                aria-label={`${drill.title}の選択を外す`}
              >
                {drill.title}
                <span aria-hidden="true">×</span>
              </button>
            ))}
          </div>
          <label className="weakness-field">
            ドリル集の名前
            <input
              maxLength={120}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              disabled={saving}
            />
          </label>
          {saveError && <SkillError message={saveError} />}
          <button
            type="button"
            className="primary-button"
            disabled={saving || !title.trim()}
            onClick={() => void createCollection()}
          >
            {saving ? "作成中…" : "選択したドリルで作成"}
          </button>
        </div>
      )}
      {saved && (
        <div className="weakness-success" role="status">
          「{saved.title}」を作成しました。{" "}
          <a className="text-link" href={`/drills/collections/${saved.id}`}>
            ドリル集を開く →
          </a>
        </div>
      )}
    </section>
  );
}
